import { useEffect, useState } from "react";
import { Reveal, SectionHeader } from "./Reveal";
import { GOOGLE_MAPS_URL } from "./Situation";
import { Stars } from "./Stars";
import { AIRBNB_REVIEWS, useLang } from "@/i18n";

/* Logos officiels (SVG inline) */
const GoogleMark = ({ className = "h-7 w-7" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z" />
    <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z" />
    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
  </svg>
);

const AIRBNB_PATH =
  "M12.001 18.275c-1.353-1.697-2.148-3.184-2.413-4.457-.263-1.027-.16-1.848.291-2.465.477-.71 1.188-1.056 2.121-1.056s1.643.345 2.12 1.063c.446.61.558 1.432.286 2.465-.291 1.298-1.085 2.785-2.412 4.458zm9.601 1.14c-.185 1.246-1.034 2.28-2.2 2.783-2.253.98-4.483-.583-6.392-2.704 3.157-3.951 3.74-7.028 2.385-9.018-.795-1.14-1.933-1.695-3.394-1.695-2.944 0-4.563 2.49-3.927 5.382.37 1.565 1.352 3.343 2.917 5.332-.98 1.085-1.91 1.856-2.732 2.333-.636.344-1.245.558-1.828.609-2.679.399-4.778-2.2-3.825-4.88.132-.345.395-.98.845-1.961l.025-.053c1.464-3.178 3.242-6.79 5.285-10.795l.053-.132.58-1.116c.45-.822.635-1.19 1.351-1.643.346-.21.77-.315 1.246-.315.954 0 1.698.558 2.016 1.007.158.239.345.557.582.953l.558 1.089.08.159c2.041 4.004 3.821 7.608 5.279 10.794l.026.025.533 1.22.318.764c.243.613.294 1.222.213 1.858zm1.22-2.39c-.186-.583-.505-1.271-.9-2.094v-.03c-1.889-4.006-3.642-7.608-5.307-10.844l-.111-.163C15.317 1.461 14.468 0 12.001 0c-2.44 0-3.476 1.695-4.535 3.898l-.081.16c-1.669 3.236-3.421 6.843-5.303 10.847v.053l-.559 1.22c-.21.504-.317.768-.345.847C-.172 20.74 2.611 24 5.98 24c.027 0 .132 0 .265-.027h.372c1.75-.213 3.554-1.325 5.384-3.317 1.829 1.989 3.635 3.104 5.382 3.317h.372c.133.027.239.027.265.027 3.37.003 6.152-3.261 4.802-6.975z";

const AirbnbMark = ({ className = "h-7 w-7" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path fill="#FF5A5F" d={AIRBNB_PATH} />
  </svg>
);

/* Agrégats réels : fiche Google + notes Airbnb (les annonces affichent 2, 2, 4 et 52 avis) */
const AIRBNB_META = [
  { rating: 5, count: 2 },
  { rating: 5, count: 2 },
  { rating: 5, count: 4 },
  { rating: 4.92, count: 52 },
];
const GOOGLE_COUNT = 12;
const TOTAL_COUNT = GOOGLE_COUNT + AIRBNB_META.reduce((s, m) => s + m.count, 0);
const AVG = (GOOGLE_COUNT * 5 + AIRBNB_META.reduce((s, m) => s + m.count * m.rating, 0)) / TOTAL_COUNT;

function Marquee({ title, items, testid, Mark }) {
  const { isFr } = useLang();
  return (
    <Reveal delay={0.08}>
      <div className="mt-14" data-testid={testid}>
        <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2">
          <h3 className="font-display text-2xl sm:text-3xl font-light text-bone flex items-center gap-3">
            {Mark && <Mark className="h-7 w-7 shrink-0" />}
            <span>{title}</span>
          </h3>
          <span className="font-mono text-[0.55rem] uppercase tracking-[0.25em] text-gold">
            {items.length} × 5 ★
          </span>
        </div>
        <div className="mt-6 border-y gold-hairline overflow-hidden" data-testid={`${testid}-viewport`}>
          <div className="marquee-track py-5">
            {[...items, ...items].map((r, i) => (
              <figure
                key={i}
                data-testid={`${testid}-card`}
                className="airbnb-review-card w-[300px] sm:w-[340px] shrink-0 border gold-hairline bg-moss/20 p-5 flex flex-col"
              >
                <div className="flex items-center justify-between gap-3">
                  <Stars size={10} />
                  <span className="font-mono text-[0.5rem] uppercase tracking-[0.2em] text-goldlight text-right flex items-center justify-end gap-1.5">
                    {Mark && <Mark className="h-3.5 w-3.5 shrink-0" />}
                    <span>
                      {r.tag}
                      {r.date ? ` · ${r.date}` : ""}
                    </span>
                  </span>
                </div>
                <blockquote className="mt-3 font-display italic font-light text-base leading-relaxed text-bone/90 clamp-5 flex-1">
                  «&nbsp;{r.q}&nbsp;»
                </blockquote>
                <figcaption className="mt-3 font-mono text-[0.55rem] uppercase tracking-[0.2em] text-sage">
                  {r.by}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
        <p className="mt-3 font-mono text-[0.55rem] uppercase tracking-[0.25em] text-sage">
          {isFr ? "Le survol met le défilement en pause" : "Hover to pause the scroll"}
        </p>
      </div>
    </Reveal>
  );
}

export default function Reviews() {
  const { t, isFr } = useLang();
  const [data, setData] = useState({ loaded: false, google: [], airbnb: [] });

  useEffect(() => {
    const API = `${process.env.REACT_APP_BACKEND_URL}/api/reviews`;
    fetch(API)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((list) => {
        if (!Array.isArray(list) || !list.length) return;
        setData({
          loaded: true,
          google: list.filter((r) => r.source === "google"),
          airbnb: list.filter((r) => r.source === "airbnb"),
        });
      })
      .catch(() => {});
  }, []);

  const gItems = data.loaded
    ? data.google.map((r) => ({ q: isFr ? r.text : r.text_en || r.text, by: r.author, tag: "Google", date: "" }))
    : t.reviewItems.map((r) => ({ q: r.q, by: r.by, tag: "Google", date: "" }));

  const avgLabel = AVG.toFixed(2).replace(".", isFr ? "," : ".");

  const aItems = data.loaded
    ? data.airbnb.map((r) => ({
        q: isFr ? r.text : r.text_en || r.text,
        by: r.author,
        tag: isFr ? r.listing_fr : r.listing_en,
        date: isFr ? r.date_fr : r.date_en,
      }))
    : AIRBNB_REVIEWS.map((r) => ({
        q: r.q,
        by: r.by,
        tag: isFr ? r.fr : r.en,
        date: isFr ? r.dateFr : r.dateEn,
      }));

  return (
    <section
      id="avis"
      data-testid="reviews-section"
      className="relative py-24 sm:py-32 px-5 sm:px-10 bg-pine/50"
    >
      <div className="mx-auto max-w-[1500px]">
        <SectionHeader eyebrow={t.reviews.eyebrow} title={t.reviews.title} align="center" />

        <Reveal delay={0.1}>
          <a
            href={GOOGLE_MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-rating-badge"
            className="mt-12 mx-auto max-w-md flex items-center justify-center gap-6 border gold-hairline px-8 py-6 hover:bg-moss/40 transition-colors duration-500"
          >
            <span
              className="font-display text-5xl sm:text-6xl font-light text-bone leading-none"
              data-testid="reviews-average"
            >
              {avgLabel}
            </span>
            <span className="text-left">
              <Stars size={13} />
              <span
                className="block font-mono text-[0.6rem] uppercase tracking-[0.25em] text-sage mt-2"
                data-testid="reviews-total"
              >
                {TOTAL_COUNT} {t.reviews.mixedCount}
              </span>
            </span>
          </a>
        </Reveal>

        <Marquee title={t.reviews.googleTitle} items={gItems} testid="google-reviews" Mark={GoogleMark} />
        <Marquee title={t.reviews.airbnbTitle} items={aItems} testid="airbnb-reviews" Mark={AirbnbMark} />
      </div>
    </section>
  );
}
